import type { ReactNode } from 'react';
import { useState } from 'react';
import BottomTabBar from './BottomTabBar';
import MoreSheet from './MoreSheet';
import { ReportProblemTrigger } from './ReportProblem';
import type { UserRole } from '../lib/auth';

interface MobileShellProps {
  currentPath: string;
  onNavigate: (hash: string) => void;
  onSignOut: () => void;
  onOpenKeyboardHelp: () => void;
  onReportProblem?: () => void;
  userEmail?: string;
  userRole?: UserRole;
  children: ReactNode;
}

/**
 * MobileShell — chrome for the mobile layout.
 *
 * Renders the pride flag gradient bar, a 56 px top bar with logo + page
 * title + a single "More" affordance, the screen content as a vertically
 * scrolling region, and a sticky bottom tab bar. Honours iOS / Android
 * safe-area insets via env() padding tokens declared in index.css.
 *
 * The desktop App.tsx renders a different shell with the existing topbar
 * and left sidebar; this component is mounted only when the viewport is
 * below the mobile breakpoint.
 */
export default function MobileShell({
  currentPath,
  onNavigate,
  onSignOut,
  onOpenKeyboardHelp,
  onReportProblem,
  userEmail,
  userRole,
  children,
}: MobileShellProps) {
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <div className="mobile-shell" data-testid="mobile-shell">
      {/* F-201 visual system: pride flag gradient bar mirrors the
          upload-ui pattern so both apps brand the same way. */}
      <div className="pride-bar" aria-hidden="true" />
      <header className="mobile-topbar" data-testid="mobile-topbar">
        <a
          className="mobile-topbar__brand"
          href="#/"
          onClick={(e) => {
            e.preventDefault();
            onNavigate('#/');
          }}
          aria-label="Brisbane Pride Moderation — home"
        >
          <img
            src="/bpf-logo.svg"
            alt=""
            width="64"
            height="38"
            className="mobile-topbar__logo"
          />
          <span className="mobile-topbar__title">Moderation</span>
        </a>
        {onReportProblem && (
          <ReportProblemTrigger
            onClick={onReportProblem}
            shortLabel="Report"
            data-testid="mobile-report-problem"
          />
        )}
      </header>
      <main className="mobile-main" id="main-content" tabIndex={-1}>
        {children}
      </main>
      <BottomTabBar
        currentPath={currentPath}
        onNavigate={onNavigate}
        onOpenMore={() => setMoreOpen(true)}
        moreOpen={moreOpen}
      />
      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onNavigate={onNavigate}
        onOpenKeyboardHelp={onOpenKeyboardHelp}
        onReportProblem={onReportProblem}
        onSignOut={onSignOut}
        userEmail={userEmail}
        userRole={userRole}
      />
    </div>
  );
}
